import React, { MouseEventHandler } from 'react';

import { Icons } from '../../../../atoms/SVG';

import extendClassNameWithModifier from '../../../../../lib/bemUtils/bem';

import Btn, { BtnModifier } from '../../../Btn';

import { Heading, HeadingHierarchy } from '../../../../../_lib/components/Heading/Heading';
import { StatusInfoForms } from '../../../../../_lib/components/StatusInfo';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { LinkingPages } from './partials/LinkingPages';
import { Hint } from '../../../../../_lib/components/Hint';
import { LinkingPageType } from '../../../../../types/api/LinkingPageType';
import LiveRegion from '../../../../atoms/LiveRegion/LiveRegion';
import { StatusInfoModifier } from '../../../../../_lib/components/StatusInfo/partials/StatusInfo';

type FormSubmitOrCancelProps = {
  interactionStatusText?: string;
  status?: 'success' | 'fail' | 'sending' | '';
  statusModifier?: StatusInfoModifier;
  ariaControls?: string;
  modifier?: string | string[];
  invertedButtons?: boolean;
  hideSubmitButton?: boolean;
  disableSubmitButton?: boolean;
  focusOnMount?: boolean;
  hint?: string;
  headline: string | React.ReactNode;
  headlineId?: string;
  hierarchy: HeadingHierarchy;
  hideHeadline?: boolean;
  acceptButtonText?: string;
  acceptButtonTitle?: string;
  acceptButtonType?: 'button' | 'submit';
  acceptFormAction?: string;
  acceptAction?: MouseEventHandler<HTMLButtonElement>;
  cancelButtonText?: string;
  cancelButtonTitle?: string;
  cancelButtonType?: 'button' | 'submit';
  cancelFormAction?: string;
  cancelAction?: MouseEventHandler<HTMLButtonElement>;
  disableCancelButton?: boolean;
  onlySubmit?: boolean;
  linkingPages?: LinkingPageType;
};

/**
 * Renders two Buttons
 */
function FormSubmitOrCancel({
  interactionStatusText,
  status,
  statusModifier,
  ariaControls,
  modifier = [],
  invertedButtons,
  hideSubmitButton,
  disableSubmitButton,
  focusOnMount,
  hint,
  headline,
  headlineId,
  hierarchy,
  hideHeadline,
  acceptButtonText,
  acceptButtonTitle,
  acceptButtonType = 'submit',
  acceptFormAction,
  acceptAction,
  cancelButtonText,
  cancelButtonTitle,
  cancelButtonType = 'button',
  cancelFormAction,
  cancelAction,
  disableCancelButton,
  onlySubmit,
  linkingPages,
}: FormSubmitOrCancelProps) {
  const { t } = useSonTranslation('FormSubmitOrCancel');

  /**
   * Will return two buttons wrapped into a <Fragment>
   */
  const renderButtons = () => {
    const buttonModifier = invertedButtons ? BtnModifier.INVERTED : undefined;
    return (
      <>
        {!hideSubmitButton && (
          <div className="form-submit-or-cancel__button-container">
            <Btn
              text={acceptButtonText || t('yes')}
              titleText={acceptButtonTitle}
              svgSpriteId={Icons.ACCEPT_CHECK}
              hiddenText
              type={acceptButtonType}
              formAction={acceptFormAction}
              onClick={acceptAction}
              cssClass="btn--single-action btn--single-action-yes"
              modifier={buttonModifier}
              ariaDisabled={status === 'success' || status === 'sending' || disableSubmitButton}
              ariaControls={ariaControls}
              focusOnMount={focusOnMount}
            />
          </div>
        )}
        {!onlySubmit && (
          <div className="form-submit-or-cancel__button-container">
            <Btn
              text={cancelButtonText || t('no')}
              titleText={cancelButtonTitle}
              svgSpriteId={Icons.DECLINE_CROSS}
              hiddenText
              type={cancelButtonType}
              formAction={cancelFormAction}
              onClick={cancelAction}
              cssClass="btn--single-action btn--single-action-no"
              modifier={buttonModifier}
              ariaDisabled={status === 'success' || status === 'sending' || disableCancelButton}
              ariaControls={ariaControls}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={extendClassNameWithModifier('form-submit-or-cancel', modifier)}>
      {hint && <Hint hint={t('hint')} message={hint} />}
      {linkingPages && <LinkingPages linkingPages={linkingPages} />}
      {status !== 'success' && (
        <div className="form-submit-or-cancel__container">
          <Heading
            hierarchy={hierarchy}
            id={headlineId}
            className={extendClassNameWithModifier('form-submit-or-cancel__headline', hideHeadline ? 'invisible' : '')}
          >
            <span className="form-submit-or-cancel__headline-content">{headline}</span>
          </Heading>
          <div className={onlySubmit ? 'form-submit-or-cancel__buttons-only-submit' : 'form-submit-or-cancel__buttons'}>
            {renderButtons()}
          </div>
        </div>
      )}
      <div className="form-submit-or-cancel__interaction-status">
        <LiveRegion invisible={false} ariaAtomic>
          {status && (
            <div className="form-submit-or-cancel__status">
              <StatusInfoForms type={status} message={interactionStatusText} modifier={statusModifier} />
            </div>
          )}
        </LiveRegion>
      </div>
    </div>
  );
}

export default FormSubmitOrCancel;
